/* eslint-disable @typescript-eslint/no-unused-vars */
/* ─── Graph rate-limiter + retry wrapper ─────────────────────────────────── */
// Beperkt het aantal Graph-API calls per seconde om HTTP 429 (throttling) te voorkomen.
// Bij een 429 wordt automatisch gewacht conform de Retry-After header en opnieuw geprobeerd.
const GRAPH_QPS = 8;            // max calls per seconde
const GRAPH_MAX_RETRIES = 4;    // hoe vaak opnieuw proberen bij 429/5xx
let gqLastCalls: number[] = []; // sliding window van timestamps

async function gThrottle(): Promise<void> {
  const now = Date.now();
  // Verwijder calls ouder dan 1 seconde
  gqLastCalls = gqLastCalls.filter(t => now - t < 1000);
  if (gqLastCalls.length >= GRAPH_QPS) {
    // Wacht tot de oudste call >1s oud is
    const wait = 1000 - (now - gqLastCalls[0]) + 5;
    await new Promise(r => setTimeout(r, wait));
    return gThrottle();
  }
  gqLastCalls.push(Date.now());
}

// Wrapper die elke Graph-call door de throttle stuurt en retry's afhandelt.
// Gebruik: await gQ(() => graphClient.api("/me").get())
// Return type is `any` omdat graphClient.api() ook any teruggeeft — anders breekt type-inference op resp.value etc.
export async function gQ(fn: () => Promise<any>, retries = GRAPH_MAX_RETRIES): Promise<any> {
  await gThrottle();
  try {
    return await fn();
  } catch (e: any) {
    const status = e?.statusCode || e?.status;
    // 429 = throttled, 503/504 = tijdelijk niet beschikbaar
    if (retries > 0 && (status === 429 || status === 503 || status === 504)) {
      // Probeer Retry-After header te lezen, anders exponential backoff
      let waitMs = 0;
      const ra = e?.response?.headers?.get?.("Retry-After") || e?.headers?.["retry-after"];
      if (ra) {
        const n = parseInt(ra, 10);
        if (!isNaN(n)) waitMs = n * 1000;
      }
      if (!waitMs) waitMs = (GRAPH_MAX_RETRIES - retries + 1) * 2000; // 2s, 4s, 6s, 8s
      await new Promise(r => setTimeout(r, waitMs));
      return gQ(fn, retries - 1);
    }
    throw e;
  }
}
